import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { VerdictBadge } from "./CertificationBadges";

export default function CertifyDialog({ periodId, onClose, onCertified }) {
  const [live, setLive] = useState(null);
  const [hasPrior, setHasPrior] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([
      api.governanceStatus(periodId),
      api.latestCertification(periodId),
      api.getSettings().catch(() => null),
    ]).then(([g, c, s]) => {
      setLive(g);
      setHasPrior(!!c);
      setSettings(s);
    }).catch(e => setError(e.message || "Failed to load"));
  }, [periodId]);

  async function submit() {
    if (hasPrior && !reason.trim()) {
      setError("A rationale is required to re-certify.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const cert = hasPrior
        ? await api.recertifyPeriod(periodId, reason.trim())
        : await api.certifyPeriod(periodId);
      onCertified?.(cert);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (!live) {
    return (
      <Modal onClose={onClose}>
        <div style={{ padding: 24 }}>Loading…</div>
      </Modal>
    );
  }

  const preparerMissing = !settings?.contact?.contact_name;
  const verb = hasPrior ? "Re-certify" : "Certify";
  const basis = hasPrior ? "recertification" : "controller_attestation";

  return (
    <Modal onClose={onClose}>
      <div style={{ padding: 24, maxWidth: 640 }} data-testid="certify-dialog">
        <h2 className="heading h2" style={{ marginBottom: 8 }}>
          {verb} period
        </h2>
        <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          The verdict below will be FROZEN onto the Certification record.
          Snapshot citations and audit cursor will also be captured.
        </div>

        {/* Live verdict */}
        <Row label="Live verdict">
          <VerdictBadge verdict={live.verdict} dataTestId="certify-live-verdict" />
        </Row>
        <Row label="Decision rule version">
          <code>{"v1.0.0"}</code>
        </Row>
        <Row label="Certification basis">
          <code data-testid="certify-basis">{basis}</code>
        </Row>

        {/* Triggers */}
        <div style={{ marginTop: 16 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700,
            letterSpacing: "0.05em", marginBottom: 6 }}>
            TRIGGERS
          </div>
          {(live.triggers || []).map(t => (
            <div key={t.code} style={{ fontSize: 12, marginBottom: 6,
              padding: "6px 10px", background: "#fafafa", borderRadius: 4,
              border: "1px solid #ececec" }}>
              <div style={{ fontWeight: 600 }}>{t.label}</div>
              <div className="muted" style={{ marginTop: 2 }}>{t.detail}</div>
            </div>
          ))}
        </div>

        {/* Preparer info */}
        <div style={{ marginTop: 16, padding: "10px 12px",
          background: preparerMissing ? "#fff4f0" : "#fafafa",
          border: `1px solid ${preparerMissing ? "#f3c2b3" : "#ececec"}`,
          borderRadius: 4 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700,
            letterSpacing: "0.05em", marginBottom: 4 }}>
            PREPARER (from Company Settings)
          </div>
          {preparerMissing ? (
            <div style={{ fontSize: 12, color: "#8a2a0c" }}>
              ⚠ Preparer name is empty in Company Settings — set it before
              certifying for the attestation to carry a name.
            </div>
          ) : (
            <div style={{ fontSize: 13 }}>
              <b>{settings.contact.contact_name}</b>
              {settings.contact.contact_position && (
                <span className="muted"> · {settings.contact.contact_position}</span>
              )}
            </div>
          )}
        </div>

        {/* Re-certification rationale */}
        {hasPrior && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Re-certification rationale (required)
            </div>
            <textarea
              data-testid="certify-rationale"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              style={{ width: "100%", padding: 8, fontSize: 13, fontFamily: "inherit" }}
              placeholder="Describe why a new certification is being recorded…"
            />
          </div>
        )}

        {error && (
          <div data-testid="certify-error" style={{ marginTop: 12, padding: "8px 12px",
            background: "#fff4f0", color: "#8a2a0c", border: "1px solid #f3c2b3",
            borderRadius: 4, fontSize: 12 }}>{error}</div>
        )}

        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} className="btn" data-testid="certify-cancel">
            Cancel
          </button>
          <button onClick={submit} className="btn primary"
            disabled={submitting} data-testid="certify-confirm">
            {submitting ? "Recording…" : `Confirm ${verb.toLowerCase()}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between",
      alignItems: "center", padding: "4px 0", fontSize: 13 }}>
      <span className="muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(20,20,20,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--surface)", borderRadius: 8,
        boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
      }}>
        {children}
      </div>
    </div>
  );
}
